/**
 * `verifyInclusionProofAttestation` — `@shn/sdk/audit` consumer-platform
 * verifier for substrate-emitted `InclusionProofAttestation` (ECDSA P-256
 * + RFC 8785 JCS + detached JWS).
 *
 * Per `sdk_v0_audit_module_design.md` (PR #391, merged) §5.2 + §5.3 + §5.4.
 *
 * # Substrate-side vs SDK consumer surface
 *
 * Substrate-side `verifyPortablePatientIdentityAssertion` at [PR #378](https://github.com/SmartHealthNetwork/SHN2026/pull/378)
 * uses Node `crypto.verify` (DER signatures) for substrate-internal verifier
 * surfaces. **This** SDK-side verifier uses Web Crypto API native
 * (`crypto.subtle.verify` — ECDSA P-256 accepts IEEE P1363 R||S signatures
 * directly; no DER conversion needed) for browser + Node 19+ portability.
 *
 * The two verifiers are **canonical-mirror Level 2** per
 * `feedback_canonical_mirror_drift_detection.md` — both consume the same
 * `@shn/audit-canonical.canonicalizeForSigning` canonicalization primitive
 * (single canonical-bytes source-of-truth per design memo §5.4); the mirror
 * is on the verification flow shape only (Web Crypto vs Node `crypto`).
 *
 * # What gets verified
 *
 * 1. The detached JWS structure is well-formed (`<protected>.<empty>.<signature>`)
 * 2. The protected header decodes as JSON with the expected fields
 * 3. The protected header `alg` is `ES256` (no algorithm-confusion attacks)
 * 4. The protected header `iss` is `'shn-pbc'` (issuer pin)
 * 5. The protected header `kid` resolves to a configured trusted attestation
 *    key (rotation-aware — `trustedAttestationKeys` is keyed by `kid`)
 * 6. The signature verifies cryptographically against the attestation's
 *    canonical bytes (everything-except-`substrateSignature`), via Web
 *    Crypto API native `crypto.subtle.verify`
 *
 * # What is NOT verified by this helper
 *
 * - **Audience binding** — attestation has `aud='*'` per design memo §4.1
 *   (publicly verifiable transparency, not per-platform-bound). No
 *   audience-replay-defense reason in the result discriminator.
 * - **Hash-vs-record verification** — `attestedRecordHash` correctness
 *   against the actual record bytes is `@shn/sdk/identity` orchestrator
 *   scope (Phase 5; separate cycle). This verifier confirms the
 *   attestation signature only.
 * - **Cross-region anchor verification** — `auditLedgerAnchor` correctness
 *   against the substrate's anchor registry is `verifyMultiRegionAnchor`
 *   scope (Phase 4.5; gates on DEV7 anchor registry primitive per design
 *   memo §6 + §7.4).
 * - **`attestedAt` freshness** — per-operation policy concern; consumer
 *   platform decides freshness window. Verifier returns `attestedAt` from
 *   the JWS protected header for caller inspection.
 */
import { canonicalizeForSigning } from '@shn/audit-canonical';
import { base64UrlDecode, base64UrlEncode } from './jws-utils.js';
/** ECDSA P-256 raw (P1363 R||S) signature length in bytes. */
const P1363_SIGNATURE_LENGTH = 64;
/**
 * Verify a substrate-emitted `InclusionProofAttestation` ECDSA P-256
 * signature using Web Crypto API native verification.
 *
 * Returns a discriminated-union result; does NOT throw on signature
 * mismatch (signature mismatch is data-shape outcome, not exceptional).
 * Throws only for caller misconfiguration:
 *
 * - Empty `trustedAttestationKeys` map (caller must supply at least one
 *   trusted key)
 * - Web Crypto API unavailable (`globalThis.crypto?.subtle` is undefined —
 *   Node <19 or browser without SubtleCrypto)
 *
 * Web Crypto API availability:
 * - Browser: Chrome 37+, Firefox 34+, Safari 11+, Edge 79+
 * - Node.js: 19+ via `globalThis.crypto.subtle`
 */
export async function verifyInclusionProofAttestation(attestation, trustedAttestationKeys) {
    if (trustedAttestationKeys.size === 0) {
        throw new Error('@shn/sdk/audit.verifyInclusionProofAttestation: trustedAttestationKeys map is empty; caller must supply at least one trusted attestation key for verification');
    }
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) {
        throw new Error('@shn/sdk/audit.verifyInclusionProofAttestation: Web Crypto API unavailable (globalThis.crypto.subtle is undefined); requires Node 19+ or a browser with SubtleCrypto');
    }
    // Step 1: structural decomposition of detached-JWS compact form.
    const segments = attestation.substrateSignature.split('.');
    if (segments.length !== 3 || segments[1] !== '') {
        return {
            valid: false,
            reason: 'malformed-jws',
            detail: 'detached JWS must be of form <protected>..<signature> with empty middle segment',
        };
    }
    const [protectedB64, , signatureB64] = segments;
    // Step 2: parse + structurally validate protected header.
    let protectedHeader;
    try {
        const headerBytes = base64UrlDecode(protectedB64);
        const headerJson = new TextDecoder('utf-8', { fatal: true }).decode(headerBytes);
        const parsed = JSON.parse(headerJson);
        if (!isAttestationProtectedHeader(parsed)) {
            return {
                valid: false,
                reason: 'malformed-protected-header',
                detail: 'protected header missing required fields (alg/kid/typ/iat/iss/aud) or wrong types',
            };
        }
        protectedHeader = parsed;
    }
    catch (err) {
        return {
            valid: false,
            reason: 'malformed-protected-header',
            detail: `protected header is not valid base64url-encoded JSON: ${err instanceof Error ? err.message : String(err)}`,
        };
    }
    // Step 3: algorithm pin (defense against alg-confusion attacks).
    if (protectedHeader.alg !== 'ES256') {
        return {
            valid: false,
            reason: 'unsupported-algorithm',
            detail: `expected alg='ES256'; got '${String(protectedHeader.alg)}'`,
        };
    }
    // Step 4: issuer pin.
    if (protectedHeader.iss !== 'shn-pbc') {
        return {
            valid: false,
            reason: 'untrusted-issuer',
            detail: `expected iss='shn-pbc'; got '${String(protectedHeader.iss)}'`,
        };
    }
    // Step 5: kid resolution.
    const attestationKey = trustedAttestationKeys.get(protectedHeader.kid);
    if (!attestationKey) {
        return {
            valid: false,
            reason: 'unknown-key-id',
            detail: `kid '${protectedHeader.kid}' not in trustedAttestationKeys map`,
        };
    }
    // Step 6: cryptographic verification.
    // Recompute canonical bytes over everything-except-`substrateSignature`
    // (symmetric with substrate emitter; `canonicalizeForSigning`'s
    // EXCLUDED_FIELDS does not include `substrateSignature`, so we exclude it
    // structurally by building the unsigned view manually). Same pattern as
    // substrate-side `verifyPortablePatientIdentityAssertion` at PR #378.
    const unsignedView = {
        attestedRecordHash: attestation.attestedRecordHash,
        attestedRecordType: attestation.attestedRecordType,
        auditLedgerAnchor: attestation.auditLedgerAnchor,
        attestedAt: attestation.attestedAt,
    };
    const canonicalUnsigned = canonicalizeForSigning(unsignedView);
    // JWS signing input is `<protected_b64>.<payload_b64>` as ASCII bytes.
    const payloadB64 = base64UrlEncode(canonicalUnsigned);
    const signingInput = new TextEncoder().encode(`${protectedB64}.${payloadB64}`);
    let signatureP1363;
    try {
        signatureP1363 = base64UrlDecode(signatureB64);
    }
    catch (err) {
        return {
            valid: false,
            reason: 'malformed-jws',
            detail: `signature segment is not valid base64url: ${err instanceof Error ? err.message : String(err)}`,
        };
    }
    if (signatureP1363.length !== P1363_SIGNATURE_LENGTH) {
        return {
            valid: false,
            reason: 'malformed-jws',
            detail: `signature is not a valid ${P1363_SIGNATURE_LENGTH}-byte P1363 R||S; got ${signatureP1363.length} bytes`,
        };
    }
    // Web Crypto API ECDSA verify accepts P1363 R||S form directly (unlike
    // Node `crypto.verify` which requires DER for EC keys). One fewer code
    // path than substrate-side per `jws-utils.ts` rationale.
    let cryptoVerified;
    try {
        cryptoVerified = await subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, attestationKey, signatureP1363, signingInput);
    }
    catch (err) {
        return {
            valid: false,
            reason: 'signature-mismatch',
            detail: `crypto.subtle.verify threw: ${err instanceof Error ? err.message : String(err)}`,
        };
    }
    if (!cryptoVerified) {
        return {
            valid: false,
            reason: 'signature-mismatch',
            detail: 'ECDSA P-256 signature does not verify against trusted attestation key for this kid',
        };
    }
    return {
        valid: true,
        verifiedKid: protectedHeader.kid,
        attestedAt: protectedHeader.iat,
    };
}
function isAttestationProtectedHeader(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return (typeof v['alg'] === 'string' &&
        typeof v['kid'] === 'string' &&
        typeof v['typ'] === 'string' &&
        typeof v['iat'] === 'number' &&
        typeof v['iss'] === 'string' &&
        typeof v['aud'] === 'string');
}
//# sourceMappingURL=attestation-verifier.js.map